import { useEffect, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import {
  appendKeepImageIds,
  getContentId,
  getInitialImageList,
  getToday,
  makePreviewImage,
} from "./contentFormUtils";

const partnerCategoryOptions = [
  { value: "FOOD", label: "식음료" },
  { value: "CULTURE", label: "문화여가" },
  { value: "LIFE", label: "생활 편의" },
];

const durationOptions = [
  { value: "LONG", label: "장기" },
  { value: "SHORT", label: "단기" },
];

function PartnerForm({
  mode = "write",
  initialData = {},
  onCancel,
  onSaveComplete,
}) {
  const isEdit = mode === "edit";

  const [companyName, setCompanyName] = useState(
    initialData?.companyName || ""
  );
  const [startDate, setStartDate] = useState(
    initialData?.startDate || getToday()
  );
  const [endDate, setEndDate] = useState(initialData?.endDate || getToday());
  const [benefitSummary, setBenefitSummary] = useState(
    initialData?.benefitSummary || ""
  );
  const [title, setTitle] = useState(
    initialData?.title || initialData?.companyName || ""
  );
  const [partnerCategory, setPartnerCategory] = useState(
    initialData?.category || "FOOD"
  );
  const [durationType, setDurationType] = useState(
    initialData?.durationType || "LONG"
  );
  const [content, setContent] = useState(initialData?.content || "");

  const [mainImgFile, setMainImgFile] = useState(null);
  const [mainImgName, setMainImgName] = useState(
    initialData?.mainImg ? "기존 대표 이미지" : ""
  );
  const [images, setImages] = useState(() => getInitialImageList(initialData));
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const hasExistingMainImg = Boolean(isEdit && initialData?.mainImg);

  useEffect(() => {
    if (!isEdit) return;

    setCompanyName(initialData?.companyName || "");
    setStartDate(initialData?.startDate || getToday());
    setEndDate(initialData?.endDate || getToday());
    setBenefitSummary(initialData?.benefitSummary || "");
    setTitle(initialData?.title || initialData?.companyName || "");
    setPartnerCategory(initialData?.category || "FOOD");
    setDurationType(initialData?.durationType || "LONG");
    setContent(initialData?.content || "");
    setMainImgFile(null);
    setMainImgName(initialData?.mainImg ? "기존 대표 이미지" : "");
    setImages(getInitialImageList(initialData));
    setImageFiles([]);
  }, [isEdit, initialData]);

  const handleMainImageChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setMainImgFile(selectedFile);
    setMainImgName(selectedFile.name);

    const previewImage = makePreviewImage(selectedFile, true);

    setImages((prev) => {
      const withoutOldMain = prev.filter((image) => !image.isMain);
      return [previewImage, ...withoutOldMain].slice(0, 5);
    });

    e.target.value = "";
  };

  const handleRemoveMainImage = () => {
    setMainImgFile(null);

    if (initialData?.mainImg) {
      setMainImgName("기존 대표 이미지");
      setImages(getInitialImageList(initialData));
      return;
    }

    setMainImgName("");
    setImages((prev) => prev.filter((image) => !image.isMain));
  };

  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const remainCount = 5 - images.length;
    const limitedFiles = selectedFiles.slice(0, Math.max(remainCount, 0));

    if (!limitedFiles.length) {
      alert("이미지는 최대 5개까지 등록할 수 있습니다.");
      return;
    }

    const newImages = limitedFiles.map((selectedFile) =>
      makePreviewImage(selectedFile, false)
    );

    setImages((prev) => [...prev, ...newImages].slice(0, 5));
    setImageFiles((prev) => [...prev, ...limitedFiles].slice(0, 5));

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const target = images[index];

    if (target?.isExisting && target?.isMain) {
      alert("기존 대표 이미지는 새 대표 이미지를 업로드하면 교체됩니다.");
      return;
    }

    const newImageIndex = images
      .slice(0, index)
      .filter((image) => !image.isExisting && !image.isMain).length;

    setImages((prev) => prev.filter((_, i) => i !== index));

    if (!target?.isMain && !target?.isExisting) {
      setImageFiles((prev) => prev.filter((_, i) => i !== newImageIndex));
    }

    if (target?.isMain) {
      setMainImgFile(null);
      setMainImgName(initialData?.mainImg ? "기존 대표 이미지" : "");
    }
  };

  const appendPartnershipImages = (formData) => {
    const shouldUseFirstImageAsMain =
      !mainImgFile && !hasExistingMainImg && imageFiles.length > 0;

    const mainImageFile =
      mainImgFile || (shouldUseFirstImageAsMain ? imageFiles[0] : null);

    if (mainImageFile) {
      formData.append("mainImgFile", mainImageFile);
    }

    const detailImageFiles = shouldUseFirstImageAsMain
      ? imageFiles.slice(1)
      : imageFiles;

    detailImageFiles.forEach((imageFile) => {
      formData.append("imageFiles", imageFile);
    });
  };

  const handleSubmit = async () => {
    if (!companyName.trim()) {
      alert("업체명을 입력하세요.");
      return;
    }

    if (!startDate || !endDate) {
      alert("제휴 기간을 선택하세요.");
      return;
    }

    if (!benefitSummary.trim()) {
      alert("혜택 내용을 입력하세요.");
      return;
    }

    if (!title.trim()) {
      alert("제목을 입력하세요.");
      return;
    }

    if (!content.trim()) {
      alert("글 내용을 입력하세요.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("companyName", companyName);
      formData.append("startDate", startDate);
      formData.append("endDate", endDate);
      formData.append("benefitSummary", benefitSummary);
      formData.append("category", partnerCategory);
      formData.append("durationType", durationType);
      formData.append("title", title);
      formData.append("content", content);

      if (isEdit) {
        appendKeepImageIds(formData, images);
      }

      appendPartnershipImages(formData);

      if (isEdit) {
        await apiClient(
          `/api/activity/partnership/${getContentId(initialData)}`,
          {
            method: "PUT",
            body: formData,
          }
        );
      } else {
        await apiClient("/api/activity/partnership", {
          method: "POST",
          body: formData,
        });
      }

      alert(isEdit ? "수정되었습니다." : "등록되었습니다.");

      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      console.error("제휴·협력 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-form-page partner-form-page">
      <h2 className="content-form-title">
        {isEdit ? "제휴·협력 수정" : "새 제휴·협력 작성"}
      </h2>

      <div className="content-form partner-form">
        <div className="content-form-row">
          <label className="content-label">업체명</label>

          <input
            className="content-input"
            type="text"
            placeholder="업체명을 입력해 주세요."
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>

        <div className="content-form-row">
          <label className="content-label">제휴 기간</label>

          <div className="content-date-range">
            <input
              className="content-input content-date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <span>~</span>

            <input
              className="content-input content-date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="content-form-row">
          <label className="content-label">혜택 내용</label>

          <input
            className="content-input"
            type="text"
            placeholder="혜택 내용을 입력해 주세요."
            value={benefitSummary}
            onChange={(e) => setBenefitSummary(e.target.value)}
          />
        </div>

        <div className="content-form-row">
          <label className="content-label">이미지</label>

          <div className="content-file-line">
            <input
              className="content-input"
              type="text"
              placeholder="협력처에 들어갈 대표 이미지를 업로드해 주세요."
              value={mainImgName}
              readOnly
            />

            {mainImgName && (
              <button
                type="button"
                className="content-small-remove"
                onClick={handleRemoveMainImage}
              >
                ×
              </button>
            )}

            <label className="content-file-select-btn">
              파일 선택
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleMainImageChange}
              />
            </label>
          </div>
        </div>

        <div className="content-section-divider" />

        <div className="content-form-row">
          <label className="content-label">제목</label>

          <input
            className="content-input"
            type="text"
            placeholder="제목을 입력해 주세요."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="content-form-row">
          <label className="content-label">카테고리</label>

          <div className="content-chip-group">
            {partnerCategoryOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  partnerCategory === option.value
                    ? "content-chip active"
                    : "content-chip"
                }
                onClick={() => setPartnerCategory(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="content-form-row">
          <label className="content-label">장/단기</label>

          <div className="content-chip-group">
            {durationOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  durationType === option.value
                    ? "content-chip active"
                    : "content-chip"
                }
                onClick={() => setDurationType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">글쓰기</label>

          <textarea
            className="content-textarea partner-main-textarea"
            placeholder="글을 작성해 주세요.&#10;(최대 5,000자까지 입력 가능합니다.)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">이미지</label>

          <div className="content-image-box">
            {images.map((img, index) => (
              <div
                className="content-image-item"
                key={`${img.imageId || img.fileName || "image"}-${index}`}
              >
                {!img.isMain && (
                  <button
                    type="button"
                    className="content-image-remove"
                    onClick={() => handleRemoveImage(index)}
                  >
                    ×
                  </button>
                )}

                {img.preview || img.url ? (
                  <img src={img.preview || img.url} alt="preview" />
                ) : (
                  <span>{img.fileName || "기존 이미지"}</span>
                )}
              </div>
            ))}

            {images.length < 5 && (
              <label className="content-image-add">
                +
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  multiple
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>
        </div>

        <div className="content-form-footer">
          <button
            type="button"
            className="content-cancel-btn"
            onClick={onCancel}
            disabled={loading}
          >
            취소
          </button>

          <button
            type="button"
            className="content-save-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PartnerForm;